import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Alert,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { postSTKPush } from '../../redux/middleware';
import PropTypes from 'prop-types';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import RestClient from '../../api/axiosInterceptor';

function PaymentMethod ({ calculateGrandTotal, onNext }) {
  const [deliveryData, setDeliveryData] = useState(() => {
    const saved = localStorage.getItem('deliveryFormData');
    return saved ? JSON.parse(saved) : {};
  });

  const [phone, setPhone] = useState(() => deliveryData.phone_number || '');
  const [amount, setAmount] = useState(() => {
    const saved = localStorage.getItem('deliveryFormData');
    if (saved) {
      const data = JSON.parse(saved);
      // Parse and clean amount
      const cleanAmount = data.totalAmount?.toString().replace(/[^0-9.]/g, '');
      console.log('Parsed amount:', cleanAmount);
      return parseFloat(cleanAmount) || 0;
    }
    return 0;
  });
  const [errors, setErrors] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const orderId = localStorage.getItem('orderId');
  const [paymentMethod, setPaymentMethod] = useState('mpesa');
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);

  const { loading, transactionData, error } = useSelector(
    (state) => state.mpesaTransaction || {}
  );

  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth?.user);
  const isAuthenticated = user?.is_authenticated;
  const navigate = useNavigate();

  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('deliveryFormData');
      if (saved) {
        const data = JSON.parse(saved);
        setPhone(data.phone_number || '');
        setAmount(data.totalAmount || '');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const formatPhoneNumber = (phone) => {
    if (phone.startsWith('0')) {
      return `254${phone.slice(1)}`;
    }
    return phone;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsProcessing(true);
    setPaymentSuccess(false);
    setPaymentFailed(false);
    setPaymentProcessing(true);

    try {
      if (!orderId) {
        throw new Error('No order ID found');
      }

      console.log(
        'Submitting payment with amount:',
        amount,
        'for order:',
        orderId
      );

      if (paymentMethod === 'mpesa') {
        const formattedPhone = formatPhoneNumber(phone);
        const userData = user || {};

        const transactionData = {
          phone_number: formattedPhone,
          amount: Number(amount),
          account_reference: `Order${orderId}`,
          user: {
            id: userData.id || 0,
            name: `${userData.first_name || ''} ${userData.last_name || ''}`,
            email: userData.email || ''
          },
          order_id: orderId,
          purpose: 'Buy Medicine',
          is_guest: false
        };

        const response = await dispatch(postSTKPush(transactionData));

        console.log('Full STK push response:', response);
        console.log('STK push dispatched, waiting for completion...');

        // For testing: Simulate successful payment after 10 seconds
        setTimeout(async () => {
          try {
            // Update order with payment information
            console.log('Fetching orders to update payment status...');
            const ordersResponse = await RestClient.get('/medication/orders/');

            if (!ordersResponse.data || !Array.isArray(ordersResponse.data)) {
              console.error('Invalid orders response:', ordersResponse);
              throw new Error('Failed to fetch orders');
            }

            // Find the specific order
            const orderIdWithoutHyphens = orderId.replace(/-/g, '');
            console.log('Comparing order IDs:');
            console.log('- Original order ID:', orderId);
            console.log('- Without hyphens:', orderIdWithoutHyphens);

            const currentOrder = ordersResponse.data.find(
              (order) =>
                order.id === orderIdWithoutHyphens || order.id === orderId
            );

            if (!currentOrder) {
              console.error(
                'Available order IDs:',
                ordersResponse.data.map((order) => order.id)
              );
              console.error(
                'Looking for ID:',
                orderIdWithoutHyphens,
                'or',
                orderId
              );
              throw new Error('Order not found. Please check ID format.');
            }

            console.log('Found order to update:', currentOrder);
            console.log('Current order items:', currentOrder.items);

            // Update the order with payment information
            const paymentUpdateData = {
              id: orderId,
              items: currentOrder.items.map((item) => {
                if (!item.drug_id && item.drug && item.drug.id) {
                  return {
                    ...item,
                    drug_id: item.drug.id
                  };
                }
                return item;
              }),
              payment_status: 'PAID',
              payment_method: paymentMethod,
              payment_details: {
                transaction_id:
									response?.CheckoutRequestID || `tx-${Date.now()}`,
                amount: Number(amount),
                phone_number: formattedPhone,
                timestamp: new Date().toISOString()
              }
            };

            // Add debugging to see what's being sent
            console.log('Items after fix:', paymentUpdateData.items);
            console.log('Updating order with payment data:', paymentUpdateData);

            await RestClient.patch(
							`/medication/orders/${orderId}/update_payment/`,
							{
							  payment_status: 'PAID',
							  payment_method: paymentMethod,
							  payment_details: {
							    transaction_id:
										response?.CheckoutRequestID || `tx-${Date.now()}`,
							    amount: Number(amount),
							    phone_number: formattedPhone,
							    timestamp: new Date().toISOString()
							  }
							}
            );
            console.log('Order payment status updated successfully');

            // Update UI
            setPaymentSuccess(true);
            setPaymentProcessing(false);

            // Navigate to next step after successful payment
            setTimeout(() => {
              onNext();
            }, 2000);
          } catch (error) {
            console.error('Error updating order payment:', error);
            console.error('Error details:', error.response?.data);
            setPaymentFailed(true);
            setPaymentProcessing(false);
          }
        }, 10000);
      }
    } catch (error) {
      console.error('Payment initiation failed:', error);
      setErrors(error.message);
      setPaymentFailed(true);
      setPaymentProcessing(false);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Container maxWidth='sm'>
      <Box sx={{ mt: 4 }}>
        <Typography variant='h4' gutterBottom>
          Payment Method
        </Typography>
        <RadioGroup
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
          sx={{ mb: 2 }}
        >
          <FormControlLabel value='mpesa' control={<Radio />} label='M-Pesa' />
          <FormControlLabel value='visa' control={<Radio />} label='Visa' />
          <FormControlLabel
            value='mastercard'
            control={<Radio />}
            label='MasterCard'
          />
          <FormControlLabel value='paypal' control={<Radio />} label='PayPal' />
          <FormControlLabel value='stripe' control={<Radio />} label='Stripe' />
        </RadioGroup>
        <form onSubmit={handleSubmit}>
          {paymentMethod === 'mpesa' && (
            <>
              <TextField
                label='Phone Number'
                variant='outlined'
                fullWidth
                margin='normal'
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
              <TextField
                label='Amount'
                variant='outlined'
                type='number'
                fullWidth
                margin='normal'
                value={amount}
                InputProps={{
								  readOnly: true,
								  startAdornment: (
  <InputAdornment position='start'>KES</InputAdornment>
								  )
                }}
                required
                disabled
              />
            </>
          )}
          {paymentMethod === 'visa' && (
            <>
              <TextField
                label='Card Number'
                variant='outlined'
                fullWidth
                margin='normal'
                placeholder='1234 5678 9012 3456'
              />
              <TextField
                label='Expiry Date'
                variant='outlined'
                fullWidth
                margin='normal'
                placeholder='MM/YY'
              />
              <TextField
                label='CVV'
                variant='outlined'
                fullWidth
                margin='normal'
                placeholder='123'
              />
            </>
          )}
          {paymentMethod === 'mastercard' && (
            <>
              <TextField
                label='Card Number'
                variant='outlined'
                fullWidth
                margin='normal'
                placeholder='1234 5678 9012 3456'
              />
              <TextField
                label='Expiry Date'
                variant='outlined'
                fullWidth
                margin='normal'
                placeholder='MM/YY'
              />
              <TextField
                label='CVV'
                variant='outlined'
                fullWidth
                margin='normal'
                placeholder='123'
              />
            </>
          )}
          {paymentMethod === 'paypal' && (
            <>
              <TextField
                label='PayPal Email'
                variant='outlined'
                fullWidth
                margin='normal'
                placeholder='your-email@example.com'
              />
            </>
          )}
          {paymentMethod === 'stripe' && (
            <>
              <TextField
                label='Card Number'
                variant='outlined'
                fullWidth
                margin='normal'
                placeholder='1234 5678 9012 3456'
              />
              <TextField
                label='Expiry Date'
                variant='outlined'
                fullWidth
                margin='normal'
                placeholder='MM/YY'
              />
              <TextField
                label='CVV'
                variant='outlined'
                fullWidth
                margin='normal'
                placeholder='123'
              />
            </>
          )}
          <Button
            variant='contained'
            color='primary'
            type='submit'
            fullWidth
            sx={{ mt: 2 }}
            disabled={isProcessing || !orderId}
          >
            {isProcessing ? 'Processing Payment...' : 'Submit Payment'}
          </Button>
        </form>

        {paymentProcessing && (
          <Alert
            severity='info'
            sx={{ mt: 2 }}
            icon={<CircularProgress size={20} />}
          >
            Payment processing. Please complete the transaction on your phone...
          </Alert>
        )}

        {paymentSuccess && (
          <Alert severity='success' sx={{ mt: 2 }}>
            Payment Successful! Proceeding to next step...
          </Alert>
        )}

        {paymentFailed && (
          <Alert severity='error' sx={{ mt: 2 }}>
            Payment failed. Please try again or choose a different payment
            method.
          </Alert>
        )}
      </Box>
    </Container>
  );
}

PaymentMethod.propTypes = {
  onNext: PropTypes.func.isRequired,
  calculateGrandTotal: PropTypes.func.isRequired
};

PaymentMethod.defaultProps = {
  calculateGrandTotal: () => 0
};

export default PaymentMethod;
